import { logger, networkBus } from '@tunasong/models'
import { entitiesApi } from './entities.js'
import type { QueuedMutation } from './mutation-queue.js'
import { createMutationQueue, replayMutationQueue } from './mutation-queue.js'

interface StoreLike {
  dispatch: (action: unknown) => unknown
}

/**
 * Subscribes to the network bus + browser online/offline events. When the
 * combined "we are online and the websocket is connected" condition flips
 * true after having been false, drain the mutation queue.
 *
 * Idempotent — safe to call once at store boot.
 */
let initialised = false

export const initReplayTrigger = (store: StoreLike): (() => void) => {
  if (initialised) {
    // Idempotent; the original cleanup is owned by the first caller.
    return () => undefined
  }
  initialised = true

  let wsConnected = true
  let wasOffline = typeof navigator !== 'undefined' && !navigator.onLine
  const queue = createMutationQueue()

  const isOnline = () =>
    (typeof navigator === 'undefined' || navigator.onLine) && wsConnected

  const dispatchQueued = async (m: QueuedMutation): Promise<unknown> => {
    // Phase 1 only knows about entitiesApi endpoints (allowlist below).
    // Future endpoints in other APIs would need a registry; YAGNI for now.
    if (m.apiName !== 'entitiesApi') {
      throw new Error(`replay-trigger: unknown apiName "${m.apiName}" for ${m.endpointName}`)
    }
    const endpoints = entitiesApi.endpoints as unknown as Record<
      string,
      { initiate: (args: unknown) => unknown }
    >
    const endpoint = endpoints[m.endpointName]
    if (!endpoint) {
      throw new Error(`replay-trigger: endpoint ${m.endpointName} not found on entitiesApi`)
    }
    const action = endpoint.initiate(m.args)
    return store.dispatch(action)
  }

  const runReplayIfTransitioned = () => {
    const online = isOnline()
    if (online && wasOffline) {
      wasOffline = false
      void replayMutationQueue({ queue, dispatch: dispatchQueued }).catch(e =>
        logger.error('replay-trigger: replayMutationQueue threw', e)
      )
      return
    }
    if (!online) {
      wasOffline = true
    }
  }

  const offWs = networkBus.on('ws-status-changed', ({ status }) => {
    wsConnected = status === 'connected'
    runReplayIfTransitioned()
  })

  const handleOnline = () => runReplayIfTransitioned()
  const handleOffline = () => {
    wasOffline = true
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
  }

  return () => {
    offWs()
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
    initialised = false
  }
}

/** Test-only: reset the init guard so tests can re-init with a fresh store. */
export const resetReplayTriggerForTests = () => {
  initialised = false
}
